import { supabase } from '../lib/supabase';
// Vercel Build Sync Verification: v1.0.1
import { checkSpam } from './anthropicClient';

/**
 * Get all wiki articles from Supabase
 */
export const getWikiArticles = async (filters = {}) => {
  try {
    let query = supabase?.from('wiki_articles')?.select('*');

    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,excerpt.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    query = query?.order('created_at', { ascending: false });

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching wiki articles:', error);
    return [];
  }
};

/**
 * Get single wiki article by ID
 */
export const getWikiArticleById = async (articleId) => {
  try {
    const { data, error } = await supabase?.from('wiki_articles')?.select('*')?.eq('id', articleId)?.single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching article:', error);
    throw error;
  }
};

/**
 * Submit article contribution
 */
export const submitArticleContribution = async (contributionData) => {
  try {
    const { data: authData } = await supabase?.auth?.getUser();
    const user = authData?.user;

    const { data, error } = await supabase?.from('article_contributions')?.insert([
      {
        user_id: user?.id || null,
        contributor_email: contributionData?.email,
        contributor_name: contributionData?.name,
        title: contributionData?.title,
        content: contributionData?.content,
        topic_category: contributionData?.category,
        tags: contributionData?.tags || [],
      },
    ])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error submitting article contribution:', error);
    throw error;
  }
};

/**
 * Save conversation as a Wiki contribution (Auto-publishing logic)
 */
export const saveConversationToWiki = async (conversationData) => {
  try {
    const messages = conversationData?.messages || [];
    const latestUserMsg = messages.filter(m => m.role === 'user').pop();
    const latestAsstMsg = messages.filter(m => m.role === 'assistant').pop();

    if (!latestUserMsg || !latestAsstMsg) return null;

    // Run Spam Check before saving
    let spamAnalysis = { spamScore: 20, buyingIntent: 50, b2bRelevance: 50, reasoning: 'Default' };
    try {
      spamAnalysis = await checkSpam(latestUserMsg.content);
    } catch (spamError) {
      console.warn('Spam check failed, continuing with defaults:', spamError);
    }

    const { data: authData } = await supabase?.auth?.getUser();
    const user = authData?.user;

    // Determine status based on spam score
    // If spam score > 70, we still save it but mark as rejected to keep records
    // If B2B Relevance is very low (< 30), mark as rejected
    let status = 'pending';
    if (spamAnalysis.spamScore > 70 || spamAnalysis.b2bRelevance < 30) {
      status = 'rejected';
    }

    // We save this as a "contribution" that defaults to pending for moderation
    const { data, error } = await supabase.from('article_contributions').insert({
      user_id: user?.id || null,
      contributor_email: user?.email || 'anonymous@b2b.codes',
      contributor_name: user?.user_metadata?.full_name || 'AI Chat User',
      title: `Q&A: ${latestUserMsg.content.substring(0, 60)}${latestUserMsg.content.length > 60 ? '...' : ''}`,
      content: `## User Question\n${latestUserMsg.content}\n\n## AI Response\n${latestAsstMsg.content}`,
      topic_category: conversationData?.context?.articleCategory || 'General',
      is_ai_generated: true,
      status: status,
      admin_notes: `AI Analysis: Spam=${spamAnalysis.spamScore}, Intent=${spamAnalysis.buyingIntent}, Relevance=${spamAnalysis.b2bRelevance}. Reasoning: ${spamAnalysis.reasoning}`
    }).select().single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving conversation to Wiki:', error);
    return null;
  }
};

/**
 * Admin: Get article contributions
 */
export const getArticleContributions = async (filters = {}) => {
  try {
    let query = supabase?.from('article_contributions')?.select('*, user_profiles(full_name, email)');

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    query = query?.order('submitted_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching article contributions:', error);
    throw error;
  }
};

/**
 * Admin: Update contribution edits
 */
export const updateContributionEdits = async (contributionId, editData) => {
  try {
    const { data, error } = await supabase
      .from('article_contributions')
      .update({
        edited_title: editData?.title,
        edited_content: editData?.content,
        edited_tags: editData?.tags || [],
        admin_notes: editData?.notes,
        updated_at: new Date()?.toISOString()
      })
      .eq('id', contributionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating contribution edits:', error);
    throw error;
  }
};

/**
 * Admin: Update contribution status
 */
export const updateContributionStatus = async (contributionId, status, notes = null) => {
  try {
    const { data, error } = await supabase.rpc('update_contribution_status', {
      contribution_id: contributionId,
      new_status: status,
      notes: notes
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating status:', error);
    throw error;
  }
};

/**
 * Admin: Publish article contribution
 */
export const publishArticleContribution = async (contributionId) => {
  try {
    const { data, error } = await supabase.rpc('publish_article_contribution', {
      contribution_id: contributionId
    });

    if (error) throw error;
    return data; // Returns the new article ID
  } catch (error) {
    console.error('Error publishing contribution:', error);
    throw error;
  }
};


// Default export removed to ensure strict named export resolution for Rollup build stability.